import React, { useEffect, useRef, useCallback } from 'react';
import { useMapContext } from '../context/MapContext';
import { useMap, FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet-draw/dist/leaflet.draw.css';

// Add custom CSS for vertex markers and to hide controls
const customVertexStyle = `
  .leaflet-editing-icon {
    border-radius: 50% !important; /* Make it round */
    width: 6px !important; /* Make it smaller */
    height: 6px !important; /* Make it smaller */
    margin-left: -3px !important; /* Adjust for smaller size */
    margin-top: -3px !important; /* Adjust for smaller size */
    background-color: #fff !important; /* White interior */
    border: 2px solid #0078FF !important; /* Blue border */
  }
  
  /* Hide the Leaflet.Draw toolbar */
  .leaflet-draw.leaflet-control {
    display: none !important;
  }
  
  /* Hide the tooltip that appears during edit mode */
  .leaflet-draw-tooltip {
    display: none !important;
  }
`;

// Append custom styles to head
const styleTag = document.createElement('style');
styleTag.textContent = customVertexStyle;
document.head.appendChild(styleTag);

// This is needed because of how React bundles assets
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const EditableMap = ({ selectedFeature, isEditing, onEditComplete }) => {
  // Ref to store the interval ID for periodic edit mode check
  const editModeIntervalRef = useRef(null);
  const map = useMap();
  const { mapRef } = useMapContext();
  const featureGroupRef = useRef(null);
  const drawnItemsRef = useRef(null);
  
  // Store the map reference
  useEffect(() => {
    if (map) {
      mapRef.current = map;
    }
  }, [map, mapRef]);
  
  // Helper function to prevent edit mode from being deactivated
  const preventEditDeactivation = useCallback((e) => {
    // Only prevent default for certain elements that might deactivate editing
    if (isEditing) {
      // For mousedown events on the map, stop propagation to prevent deactivating edit mode
      if (e.type === 'mousedown') {
        // Don't interfere with marker dragging
        const isMarker = e.target.closest('.leaflet-marker-icon');
        if (!isMarker) {
          e.stopPropagation();
        }
      }
    }
  }, [isEditing]);

  // Method to maintain edit mode on the polygon without triggering toolbar UI
  const maintainEditMode = useCallback(() => {
    try {
      // Keep the polygon selected and in edit mode
      if (drawnItemsRef.current) {
        try {
          // Re-trigger the editing state on the polygon
          if (drawnItemsRef.current.editing && drawnItemsRef.current.editing.enabled) {
            // First check if already enabled
            if (!drawnItemsRef.current.editing.enabled()) {
              drawnItemsRef.current.editing.enable();
            }
          }
        } catch (e) {
          console.error('Error enabling polygon edit:', e);
        }
      }
      
      // Ensure edit mode persists by adding event listeners to prevent accidental closing
      const mapContainer = document.querySelector('.leaflet-container');
      if (mapContainer) {
        // Remove any existing listeners to prevent duplicates
        mapContainer.removeEventListener('mousedown', preventEditDeactivation);
        mapContainer.removeEventListener('mousemove', preventEditDeactivation);
        
        // Add fresh listeners
        mapContainer.addEventListener('mousedown', preventEditDeactivation, true);
        mapContainer.addEventListener('mousemove', preventEditDeactivation, true);
      }
    } catch (error) {
      console.error('Error maintaining edit mode:', error);
    }
  }, [preventEditDeactivation]);
  



  
  // Function to check and ensure edit mode stays active
  const checkEditMode = useCallback(() => {
    if (isEditing && drawnItemsRef.current) {
      // Ensure the polygon is still in edit mode
      if (drawnItemsRef.current.editing) {
        try {
          if (!drawnItemsRef.current.editing.enabled()) {
            drawnItemsRef.current.editing.enable();
          }
        } catch (e) {
          // Silent catch - just trying to re-enable
        }
      }
    }
  }, [isEditing]);

  // Setup the edit layer when editing begins
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // If we have a selected feature and we're in edit mode
    if (isEditing && selectedFeature && featureGroupRef.current) {
      // Clear the feature group
      featureGroupRef.current.clearLayers();
      
      try {
        // Create a new polygon from the feature geometry
        const coords = selectedFeature.feature.geometry.coordinates[0];
        const latLngs = coords.map(coord => [coord[1], coord[0]]);
        
        const polygon = L.polygon(latLngs, {
          color: 'red',
          weight: 3,
          opacity: 0.8,
          fillColor: getColorForDUN(selectedFeature.feature.properties.DUN_CODE),
          fillOpacity: 0.5
        });
        
        // Add to feature group for editing
        featureGroupRef.current.addLayer(polygon);
        drawnItemsRef.current = polygon;
        
        // Enable editing immediately on this polygon
        if (polygon.editing) {
          polygon.editing.enable();
          
          // Maintain edit mode for the polygon
          setTimeout(() => {
            maintainEditMode();
          }, 100);
        }
        
        // Fit bounds to the feature
        map.fitBounds(polygon.getBounds());
      } catch (error) {
        console.error('Error setting up edit layer:', error);
      }
    }
    
    // Clean up function for when editing is turned off
    return () => {
      if (featureGroupRef.current) {
        if (drawnItemsRef.current && drawnItemsRef.current.editing) {
          drawnItemsRef.current.editing.disable();
        }
      }
    };
  }, [isEditing, selectedFeature, map]);
  
  // Handle the feature group being created
  const handleFeatureGroupReady = (reactFGref) => {
    featureGroupRef.current = reactFGref;
  };
  
  // Helper function to get color for DUN
  function getColorForDUN(dunCode) {
    // Use the standardized DUN_CODE field
    const colorMap = {
      'N.13': '#ff7f00', // Orange
      'N.14': '#377eb8', // Blue
      'N.15': '#4daf4a', // Green
      'N.16': '#e41a1c', // Red
      'N.17': '#984ea3', // Purple
      'N.18': '#ffff33', // Yellow
      'N.19': '#a65628', // Brown
      'N.20': '#f781bf'  // Pink
    };
    
    return colorMap[dunCode] || '#999999';
  }
  
  // Track vertex movements without completing edit
  const onEditMove = useCallback((e) => {
    // Prevent accidental deactivation of edit mode during vertex movement
    // Without stopping propagation, events might bubble up and deactivate editing
    if (e && e.originalEvent) {
      e.originalEvent.stopPropagation();
    }
    
    // Re-enable edit controls if they've disappeared
    const editButton = document.querySelector('.leaflet-draw-edit-edit');
    if (!editButton || !editButton.classList.contains('leaflet-draw-edit-edit-enabled')) {
      // If controls are gone, re-activate them (without disrupting the current edit)
      setTimeout(() => {
        try {
          // Check if we're still in editing mode before re-activating
          if (isEditing && drawnItemsRef.current) {
            // Gently re-enable the edit mode without interrupting the current edit
            const editButtons = document.querySelectorAll('.leaflet-draw-toolbar a');
            editButtons.forEach(button => {
              if (button.title && button.title.includes('Edit layers') && 
                  !button.classList.contains('leaflet-draw-edit-edit-enabled')) {
                button.click();
              }
            });
          }
        } catch (error) {
          console.error('Error re-enabling edit mode:', error);
        }
      }, 10);
    }
  }, [isEditing]);
  
  // Handle edited events - using useCallback to make it stable for useEffect dependencies
  // This should only be called when the edit is explicitly finished, not during dragging
  // This is called when the "Finish Editing" button is clicked
  const onEdited = useCallback((e) => {
    // We should only complete the edit when the "Finish Editing" button is clicked
    // This ensures that dragging vertices doesn't trigger early completion
    if (isEditing && drawnItemsRef.current) {
      try {
        // Convert edited polygon to GeoJSON
        const geoJSON = drawnItemsRef.current.toGeoJSON();
        
        // Important: Update the display of the edited polygon to reflect new shape
        if (featureGroupRef.current) {
          // Ensure the displayed polygon has the updated coordinates
          drawnItemsRef.current.setStyle({
            color: 'red',
            weight: 3,
            opacity: 0.8,
            fillColor: selectedFeature ? getColorForDUN(selectedFeature.feature.properties.KODDUN) : '#ff7f00',
            fillOpacity: 0.5
          });
        }
        
        // Send the updated geometry to the parent component
        // This is what triggers the finish editing action
        if (onEditComplete && geoJSON && geoJSON.geometry) {
          onEditComplete(geoJSON.geometry);
        }
      } catch (error) {
        console.error('Error handling edit:', error);
      }
    }
  }, [isEditing, onEditComplete, selectedFeature]);
  
  // Detect changes in the polygon during editing
  useEffect(() => {
    if (isEditing && drawnItemsRef.current) {
      // Listen for ongoing vertex movements without completing edit
      drawnItemsRef.current.on('edit', onEditMove);
      drawnItemsRef.current.on('editmove', onEditMove);
      
      // Apply edit mode maintenance
      maintainEditMode();
      
      // Set up a persistent interval to check polygon edit state
      // Clear any existing interval first
      if (editModeIntervalRef.current) {
        clearInterval(editModeIntervalRef.current);
      }
      
      // Check every 1 second if edit mode is still active
      editModeIntervalRef.current = setInterval(checkEditMode, 1000);
      
      return () => {
        // Clean up listeners
        if (drawnItemsRef.current) {
          drawnItemsRef.current.off('edit', onEditMove);
          drawnItemsRef.current.off('editmove', onEditMove);
        }
        
        // Remove the map container event listeners
        const mapContainer = document.querySelector('.leaflet-container');
        if (mapContainer) {
          mapContainer.removeEventListener('mousedown', preventEditDeactivation);
          mapContainer.removeEventListener('mousemove', preventEditDeactivation);
        }
        
        // Clear the interval
        if (editModeIntervalRef.current) {
          clearInterval(editModeIntervalRef.current);
          editModeIntervalRef.current = null;
        }
      };
    } else if (!isEditing && editModeIntervalRef.current) {
      // Clean up interval if editing is turned off
      clearInterval(editModeIntervalRef.current);
      editModeIntervalRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, onEditMove, preventEditDeactivation, maintainEditMode]);
  
  return (
    <FeatureGroup ref={handleFeatureGroupReady}>
      {isEditing && (
        <EditControl
          // Use a valid position but hide with CSS
          position="topright"
          onEdited={onEdited}
          onEditStop={onEdited}
          edit={{
            edit: true, // Enable editing
            remove: false, // Disable removal
            poly: {
              allowIntersection: false
            },
            // Edit options for the selected polygon
            selectedPathOptions: {
              maintainColor: true, // Maintain the original color when editing
              dashArray: '5, 5' // Dashed line when editing
            },
            // Additional settings to hide UI elements
            showToolbar: false,
            hideToolbar: true
          }}
          draw={false} // Completely disable drawing controls
        />
      )}
    </FeatureGroup>
  );
};

export default EditableMap;
